import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Stamp, Upload, Trash2, Loader2, Save, AlertTriangle, ImageIcon,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PM Spec V3 13/05/2026 — AdminCompanyStampSettings
 *
 * Configure le cachet entreprise global. Lu/écrit via SystemConfiguration.branding.
 * Le cachet sera composé automatiquement avec les signatures de l'opérateur Baobab
 * (party=baobab_operator) sur les bons signés.
 */
const SIZES = [
  { value: 'small', label: 'Petit' },
  { value: 'medium', label: 'Moyen' },
  { value: 'large', label: 'Grand' },
];

const AdminCompanyStampSettings = () => {
  const [stamp, setStamp] = useState(null);
  const [autoApply, setAutoApply] = useState(true);
  const [size, setSize] = useState('medium');
  const [uploadedAt, setUploadedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: 'Bearer cookie-auth' };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // Read existing config (admin only)
        const r = await axios.get(`${API}/system-config/section/branding`, { headers });
        const branding = r.data?.data || r.data || {};
        setStamp(branding.company_stamp_base64 || null);
        setAutoApply(branding.company_stamp_apply_auto !== false);
        setSize(branding.company_stamp_size || 'medium');
        setUploadedAt(branding.company_stamp_uploaded_at || null);
      } catch (err) {
        // Fallback to public branding
        try {
          const pub = await axios.get(`${API}/system-config/branding/public`);
          const b = pub.data || {};
          setStamp(b.company_stamp_base64 || null);
          setAutoApply(b.company_stamp_apply_auto !== false);
          setSize(b.company_stamp_size || 'medium');
        } catch { /* noop */ }
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1024 * 1024 * 2) {
      toast({ title: 'Image trop lourde (max 2 Mo)', variant: 'destructive' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setStamp(reader.result);
    };
    reader.readAsDataURL(f);
  };

  const save = async () => {
    setSaving(true);
    try {
      // PUT into branding section (system_config update_system_configuration)
      // Read current config first to merge
      const cur = await axios.get(`${API}/system-config/`, { headers });
      const config = cur.data || {};
      const branding = { ...(config.branding || {}) };
      branding.company_stamp_base64 = stamp;
      branding.company_stamp_apply_auto = autoApply;
      branding.company_stamp_size = size;
      if (stamp && !uploadedAt) {
        branding.company_stamp_uploaded_at = new Date().toISOString();
      }
      const updated = { ...config, branding };
      await axios.put(`${API}/system-config/`, updated, { headers });
      toast({ title: 'Cachet enregistré' });
      setUploadedAt(branding.company_stamp_uploaded_at || new Date().toISOString());
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      toast({ title: 'Échec', description: detail, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const removeStamp = () => {
    setStamp(null);
    setUploadedAt(null);
  };

  return (
    <div className="space-y-4 max-w-3xl mx-auto p-4 sm:p-6" data-testid="admin-stamp-settings">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Stamp className="w-5 h-5 text-emerald-600" />
            Cachet d'entreprise
          </CardTitle>
          <CardDescription>
            Le cachet sera apposé automatiquement sur les signatures de validation
            à distance des opérateurs Baobab Connect.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : (
            <>
              {/* Aperçu */}
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 bg-slate-50 flex items-center justify-center min-h-[200px]">
                {stamp ? (
                  <img
                    data-testid="stamp-preview"
                    src={stamp}
                    alt="Cachet entreprise"
                    className="max-h-40 max-w-full object-contain"
                  />
                ) : (
                  <div className="text-center text-slate-400">
                    <ImageIcon className="w-10 h-10 mx-auto mb-2" />
                    <div className="text-sm">Aucun cachet configuré</div>
                  </div>
                )}
              </div>

              {/* Actions upload */}
              <div className="flex flex-wrap gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml"
                  onChange={onFile}
                  className="hidden"
                  data-testid="stamp-file-input"
                />
                <Button onClick={() => fileRef.current?.click()}
                        data-testid="stamp-upload-btn"
                        className="bg-emerald-600 hover:bg-emerald-700 gap-1.5">
                  <Upload className="w-4 h-4" /> {stamp ? 'Remplacer' : 'Téléverser'}
                </Button>
                {stamp && (
                  <Button variant="outline"
                          data-testid="stamp-remove-btn"
                          onClick={removeStamp}
                          className="text-red-600 hover:bg-red-50 hover:text-red-700 gap-1.5">
                    <Trash2 className="w-4 h-4" /> Supprimer
                  </Button>
                )}
              </div>

              {/* Options */}
              <div className="space-y-3 pt-2 border-t border-slate-100">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="stamp-auto-apply"
                    checked={autoApply}
                    onChange={(e) => setAutoApply(e.target.checked)}
                    className="w-4 h-4 accent-emerald-600"
                  />
                  <span className="text-sm">
                    <strong>Appliquer automatiquement</strong> sur les signatures opérateur
                  </span>
                </label>

                <div>
                  <Label className="text-sm font-medium">Taille du cachet sur la signature</Label>
                  <div className="flex gap-2 mt-2" data-testid="stamp-size-group">
                    {SIZES.map((s) => (
                      <button
                        key={s.value} type="button"
                        data-testid={`stamp-size-${s.value}`}
                        onClick={() => setSize(s.value)}
                        className={`px-4 py-2 rounded-md text-sm border-2 transition-all ${
                          size === s.value
                            ? 'bg-emerald-600 text-white border-emerald-600'
                            : 'bg-white border-slate-200 hover:border-emerald-400'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Save button */}
              <div className="flex justify-end pt-2 border-t border-slate-100">
                <Button onClick={save} disabled={saving}
                        data-testid="stamp-save-btn"
                        className="bg-emerald-600 hover:bg-emerald-700 gap-1.5">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Enregistrer
                </Button>
              </div>

              {!stamp && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-md border border-amber-200">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <div className="text-xs text-amber-800">
                    Sans cachet configuré, les signatures de validation distante
                    seront enregistrées sans cachet (tracé seul).
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminCompanyStampSettings;
